import { Input, Select, Typography, Field, Icon, Checkbox, Button } from '@/components';
import { UseFormRegister, UseFormWatch, FieldErrors } from 'react-hook-form';
import { CreateOpportunityFormFields, ProjectForm } from './form';

type ProjectFieldsProps = {
  readonly index: number;
  readonly register: UseFormRegister<CreateOpportunityFormFields>;
  readonly errors: FieldErrors<CreateOpportunityFormFields>;
  readonly remove?: () => void;
  readonly watch: UseFormWatch<CreateOpportunityFormFields>;
  readonly countriesOptions: { label: string; value: string }[];
};

export function ProjectFields({ index, register, errors, remove, watch, countriesOptions }: ProjectFieldsProps) {
  const techList: { key: keyof ProjectForm; label: string; icon: string }[] = [
    { key: 'solar', label: 'Solar', icon: 'solar' },
    { key: 'wind', label: 'Wind', icon: 'wind' },
    { key: 'bess', label: 'BESS', icon: 'bess' },
    { key: 'biogas', label: 'Biogas', icon: 'biogas' },
    { key: 'hydrogen', label: 'Green Hydrogen', icon: 'hydrogen' },
    { key: 'hidropower', label: 'Hydropower', icon: 'hidropower' },
    { key: 'datacenter', label: 'Data Center', icon: 'datacenter' },
    { key: 'topco', label: 'TopCo', icon: 'topco' },
  ];

  const project = watch(`projects.${index}`);
  const isTopco = project?.topco?.enabled === true;

  return (
    <div className="relative mt-8 space-y-4">
      <hr />
      <div className="flex items-start justify-between">
        <Typography as="h5" size="body-1" color="primary">
          {isTopco ? 'Company Details' : `Project ${index + 1}`}
        </Typography>
        {remove && <Button color="secondary" icon="trash" onClick={remove}></Button>}
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:space-x-4">
        <Field
          id={`projects.${index}.country`}
          label="Country"
          error={errors?.projects?.[index]?.country}
          classes={{ field: 'w-full sm:w-1/2' }}
        >
          <Select
            id={`projects.${index}.country`}
            registration={register(`projects.${index}.country`, { required: 'Required' })}
            options={countriesOptions}
          />
        </Field>

        {!isTopco && (
          <Field
            id={`projects.${index}.city`}
            label="City"
            error={errors?.projects?.[index]?.city}
            classes={{ field: 'w-full sm:w-1/2' }}
          >
            <Input
              id={`projects.${index}.city`}
              color={errors?.projects?.[index]?.city ? 'error' : 'primary'}
              registration={register(`projects.${index}.city`, {
                required: isTopco ? false : 'This field is required',
              })}
            />
          </Field>
        )}
      </div>

      <div>
        <Typography as="h5" size="body-3" weight="medium" className="mt-6">
          Technology
        </Typography>
        {errors?.projects?.[index]?.solar?.enabled &&
          errors?.projects?.[index]?.wind?.enabled &&
          errors?.projects?.[index]?.bess?.enabled &&
          errors?.projects?.[index]?.biogas?.enabled &&
          errors?.projects?.[index]?.hydrogen?.enabled &&
          errors?.projects?.[index]?.hidropower?.enabled &&
          errors?.projects?.[index]?.datacenter?.enabled &&
          errors?.projects?.[index]?.topco?.enabled && (
            <Typography size="body-3" color="error" className="mb-4">
              You must select at least one technology.
            </Typography>
          )}
        <div className="mt-2 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {techList.map((tech) => (
            <Checkbox
              key={tech.key}
              id={`projects.${index}.${tech.key}.enabled`}
              registration={register(
                `projects.${index}.${tech.key}.enabled` as keyof CreateOpportunityFormFields,
                tech.key === 'solar' ||
                  'wind' ||
                  'bess' ||
                  'biogas' ||
                  'hydrogen' ||
                  'hidropower' ||
                  'datacenter' ||
                  'topco'
                  ? {
                      validate: () => {
                        const isAnySelected = techList.some((t) => {
                          const value = project?.[t.key];
                          return typeof value === 'object' && value?.enabled === true;
                        });
                        return isAnySelected || 'You must select at least one technology.';
                      },
                    }
                  : undefined
              )}
              color={
                errors?.projects?.[index]?.solar?.enabled &&
                errors?.projects?.[index]?.wind?.enabled &&
                errors?.projects?.[index]?.bess?.enabled &&
                errors?.projects?.[index]?.biogas?.enabled &&
                errors?.projects?.[index]?.hydrogen?.enabled &&
                errors?.projects?.[index]?.hidropower?.enabled &&
                errors?.projects?.[index]?.datacenter?.enabled &&
                errors?.projects?.[index]?.topco?.enabled
                  ? 'error'
                  : 'primary'
              }
              classes={{ main: 'items-center' }}
            >
              <div className="flex items-center gap-x-2">
                <Icon name={tech.icon as any} className="text-primary-700 h-6 w-6" />
                <span>{tech.label}</span>
              </div>
            </Checkbox>
          ))}
        </div>
      </div>
    </div>
  );
}
